<div class="flex flex-col h-full max-h-[80vh]">
  <!-- Header -->
  <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
    <div class="flex items-center gap-3">
      <h2 class="text-base font-semibold text-gray-900">
        {{ isViewMode ? 'Indikator anzeigen' : 'Indikator bearbeiten' }}
      </h2>
      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
        {{ getIndikatorDisplayNumber() }}
      </span>
      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white" [style.background-color]="computedStatusColor">
        {{ computedStatusId === '1' ? 'Kritisch' : (computedStatusId === '2' ? 'Warnung' : (computedStatusId === '3' ? 'OK' : '—')) }}
      </span>
    </div>
    <button 
      (click)="onClose()"
      mat-icon-button
      class="text-gray-400 hover:text-gray-600">
      <mat-icon class="w-4 h-4">close</mat-icon>
    </button>
  </div>

  <!-- Scrollable Content -->
  <div class="flex-1 overflow-y-auto px-4 py-4">

  <!-- Preview Information (Edit Mode Only) -->
  <div *ngIf="isEditMode" class="mb-6 p-4 bg-blue-50 rounded-lg">
    <h3 class="text-sm font-medium text-blue-900 mb-3">
      {{ previewInfo.isFirstSave ? 'Wird gespeichert als:' : 'Bereits gespeichert:' }}
    </h3>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">
          {{ previewInfo.isFirstSave ? 'Wird geprüft von' : 'Geprüft von' }}
        </label>
        <div class="text-sm text-blue-900">{{ previewInfo.willBeCheckedBy }}</div>
      </div>
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">
          {{ previewInfo.isFirstSave ? 'Wird geprüft am' : 'Geprüft am' }}
        </label>
        <div class="text-sm text-blue-900">{{ previewInfo.willBeCheckedAt }}</div>
      </div>
    </div>
    
    <!-- Additional info for first save -->
    <div *ngIf="previewInfo.isFirstSave" class="mt-3 p-3 bg-blue-100 rounded-md">
      <div class="flex items-center">
        <mat-icon class="text-blue-600 mr-2">info</mat-icon>
        <span class="text-sm text-blue-800">
          Beim ersten Speichern werden Datum, Uhrzeit und Prüfer automatisch gesetzt.
        </span>
      </div>
    </div>
  </div>

  <!-- Indikator Info -->
  <div class="mb-6 p-4 bg-gray-50 rounded-lg">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Objekt</label>
        <div class="text-sm text-gray-900">
          <div class="font-medium">{{ data.indikator.gebaeude_name }}</div>
          <div class="text-gray-600">{{ data.indikator.sektion_name }} - {{ data.indikator.room_name }}</div>
        </div>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Indikator Details</label>
        <div class="text-sm text-gray-900">
          <div class="font-medium">{{ data.indikator.indikator_name }}</div>
          <div class="text-gray-600">Schwelle: {{ data.indikator.indikator?.schwellenwert || '-' }} {{ data.indikator.indikator?.einheit || '' }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Completion Info (View Mode Only) -->
  <div *ngIf="isViewMode && data.indikator.checked" class="mb-6 p-4 bg-blue-50 rounded-lg">
    <h3 class="text-sm font-medium text-blue-900 mb-3">Bearbeitungsinformationen</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">Geprüft am</label>
        <div class="text-sm text-blue-900">{{ getFormattedCheckedDate() }}</div>
      </div>
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">Geprüft von</label>
        <div class="text-sm text-blue-900">{{ getCheckedByUserName() }}</div>
      </div>
    </div>
  </div>

  <!-- Form -->
  <form [formGroup]="indikatorForm" class="space-y-4">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Wert -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Wert *</label>
        <input 
          type="number"
          formControlName="wert"
          placeholder="0"
          [readonly]="isViewMode"
          [class]="isViewMode ? 'w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600' : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'">
        <div *ngIf="indikatorForm.get('wert')?.invalid && indikatorForm.get('wert')?.touched" 
             class="text-red-600 text-xs mt-1">
          Wert ist erforderlich
        </div>
      </div>
    </div>

    <!-- Problem (Abweichungen) - hidden for now -->
    <div *ngIf="false">
      <app-multi-select
        [items]="abweichungenItems"
        [selectedItems]="selectedAbweichungen"
        [loading]="abweichungenLoading"
        [label]="'Problem (Abweichungen)'"
        [placeholder]="'Abweichungen auswählen...'"
        (selectionChange)="onAbweichungenChange($event)">
      </app-multi-select>
    </div>

    <!-- Maßnahme -->
    <div>
      <app-multi-select
        [items]="massnahmenItems"
        [selectedItems]="selectedMassnahmen"
        [loading]="massnahmenLoading"
        [label]="'Maßnahmen'"
        [placeholder]="'Maßnahmen auswählen...'"
        (selectionChange)="onMassnahmenChange($event)">
      </app-multi-select>
    </div>

    <!-- Interne Notiz (Artikel) -->
    <div>
      <app-multi-select
        [items]="artikelItems"
        [selectedItems]="selectedArtikel"
        [loading]="artikelLoading"
        [label]="'Artikel'"
        [placeholder]="'Artikel auswählen...'"
        (selectionChange)="onArtikelChange($event)">
      </app-multi-select>
    </div>

    <!-- Anmerkung -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Anmerkung</label>
      <textarea 
        formControlName="anmerkung"
        placeholder="Anmerkung eingeben..."
        rows="2"
        [readonly]="isViewMode"
        [class]="isViewMode ? 'w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 resize-none' : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none'"></textarea>
    </div>
  </form>
  </div>

  <!-- Fixed Actions -->
  <div class="flex justify-end gap-3 px-4 py-3 border-t border-gray-200 bg-white">
    
    <!-- View Mode Buttons -->
    <div *ngIf="isViewMode" class="flex gap-3">
      <button 
        (click)="onClose()"
        mat-button
        class="text-gray-600 hover:text-gray-800">
        Schließen
      </button>
      <button 
        (click)="switchToEditMode()"
        mat-raised-button
        color="primary"
        class="bg-blue-600 hover:bg-blue-700 text-white">
        Bearbeiten
      </button>
    </div>

    <!-- Edit Mode Buttons -->
    <div *ngIf="isEditMode" class="flex gap-3">
      <button 
        (click)="onClose()"
        mat-button
        class="text-gray-600 hover:text-gray-800">
        Abbrechen
      </button>
      <button 
        (click)="onSave()"
        [disabled]="loading || !indikatorForm.valid"
        mat-raised-button
        color="primary"
        class="bg-blue-600 hover:bg-blue-700 text-white">
        <mat-icon *ngIf="loading" class="animate-spin mr-2">refresh</mat-icon>
        <span *ngIf="loading">Speichern...</span>
        <span *ngIf="!loading">Speichern</span>
      </button>
    </div>

  </div>
</div>
